import { jwt_token } from "@arenko-group/arenko-ui/dist/helpers";
import { dynamicUrl } from "@arenko-group/arenko-ui/dist/helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import logger from "@/helpers/logger";
import type { BlockScheduleDetails } from "./createBlockScheduleApi";

const blockInputSockets: Record<string, Promise<WebSocket>> = {};

export const getBlockInputSocket = (assetId: string) => {
  const baseUrl = dynamicUrl("block-input", assetId);

  if (!(assetId in blockInputSockets)) {
    const promise = new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(baseUrl);

      ws.addEventListener("open", () => {
        resolve(ws);
      });
      ws.addEventListener("error", () => {
        const error = new Error(
          `WebSocket connection error for Block Input asset ${assetId}`
        );
        reject(error);
      });
    });

    blockInputSockets[assetId] = promise;
  }
  return blockInputSockets[assetId];
};

export const blockInputApi = createApi({
  reducerPath: "blockInputApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/block-input/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${jwt_token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getBlockInputMessages: build.query<BlockScheduleDetails[], string>({
      queryFn: () => ({
        data: [],
      }),
      async onCacheEntryAdded(
        assetId: string | undefined,
        {
          cacheDataLoaded,
          cacheEntryRemoved,
          updateCachedData,
          dispatch,
          getState: _getState,
        }
      ) {
        if (!assetId) return;

        // Set loading state to true when starting WebSocket connection
        dispatch(
          blockInputApi.util.updateQueryData(
            "getBlockInputMessages",
            assetId,
            () => []
          )
        );

        try {
          const ws = await getBlockInputSocket(assetId);
          await cacheDataLoaded;

          // WebSocket connected successfully, but we're still waiting for data
          ws.onmessage = (event: MessageEvent) => {
            try {
              const message = JSON.parse(event.data);

              // Block input sends messages with type and data
              if (message.type === "schedules" && Array.isArray(message.data)) {
                updateCachedData(() => message.data as BlockScheduleDetails[]);
              }
            } catch (error) {
              logger.error(
                "Error parsing block input websocket message",
                error
              );
            }
          };

          ws.onclose = (event) => {
            if (event.code !== 1000) {
              logger.warn(
                `WebSocket closed unexpectedly for asset ${assetId}. Code: ${event.code}, Reason: ${event.reason}`
              );
            }
          };
        } catch (err) {
          logger.error(
            `There was an error while handling block input websocket for asset ${assetId}`,
            err
          );
        }

        await cacheEntryRemoved;
        if (assetId in blockInputSockets) {
          const ws = await blockInputSockets[assetId];
          ws.close();
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete blockInputSockets[assetId];
        }
      },
    }),
  }),
});

export const { useGetBlockInputMessagesQuery } = blockInputApi;
